import { inject, Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { UserEnvService, type RolePermissionRow } from '@core/services/user-env/user-env.service';
import { isAppOrTokenRoleAdmin, keycloakTokenHasRole, userEnvPrimaryRoleType } from '@core/utils/keycloak-app-admin';

/**
 * Admin/supervisor bypass; otherwise a grant is shown only when its `auth_num` is present
 * in `role_permission` on user-env. Fails closed (hidden) when that data isn't loaded yet.
 */
@Injectable({ providedIn: 'root' })
export class UserActionAuthService {
  private readonly keycloak = inject(Keycloak);
  private readonly userEnv = inject(UserEnvService);

  canShowAction(authNum: string): boolean {
    if (this.isAdminOrSupervisor()) return true;

    const perms: RolePermissionRow[] | undefined = this.userEnv.env()?.role_permission;
    if (perms == null) return false;
    return perms.some((p) => p.auth_num === authNum);
  }

  /**
   * Menu-visibility variant of {@link canShowAction}: a prefix match, so a parent menu node
   * shows whenever any granted `auth_num` is nested under it (e.g. a `'02.02.03.001'` grant
   * makes `'02'`, `'02.02'`, and `'02.02.03'` all visible too).
   */
  canShowMenuItem(authNum: string): boolean {
    if (this.isAdminOrSupervisor()) return true;

    const perms: RolePermissionRow[] | undefined = this.userEnv.env()?.role_permission;
    if (perms == null) return false;
    return perms.some((p) => typeof p.auth_num === 'string' && p.auth_num.startsWith(authNum));
  }

  /** True when the API actually provided `role_permission` (fine-grained data present, even if empty). */
  hasRolePermissionData(): boolean {
    return Array.isArray(this.userEnv.env()?.role_permission);
  }

  /** True for admin/supervisor regardless of any specific `auth_num` grant. */
  isAdminOrSupervisor(): boolean {
    const tokenParsed = this.keycloak.tokenParsed;
    const env = this.userEnv.env();

    if (isAppOrTokenRoleAdmin(tokenParsed, env)) return true;
    if (keycloakTokenHasRole(tokenParsed, 'ROLE_SUPERVISOR')) return true;
    return userEnvPrimaryRoleType(env) === 'ROLE_SUPERVISOR';
  }
}
